import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import { ErrorBoundary } from "@/components/pawkt/ErrorBoundary";
import { PawktSplash } from "@/components/pawkt/PawktLogo";
import Login from "@/pages/Login";
import Activate from "@/pages/Activate";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Checkout from "@/pages/Checkout";
import {
  PublicLayout, Home, Products, Pricing, ShippingPolicy, RefundPolicy,
  Terms, Privacy, Contact, Demo,
} from "@/pages/public/PublicSite";
import PetCompassPage from "@/pages/public/PetCompassPage";
import BookPage from "@/pages/public/BookPage";
import SelfBooking from "@/pages/selfbooking/SelfBooking";
import PlatformPhoneNumbers from "@/pages/platform/PhoneNumbers";
import ShallyGreetings from "@/pages/platform/ShallyGreetings";
import ShallyPilot from "@/pages/platform/ShallyPilot";
import BusinessOnboarding from "@/pages/platform/BusinessOnboarding";
import BusinessOutbound from "@/pages/platform/BusinessOutbound";
import PlatformSalesLeads from "@/pages/platform/SalesLeads";
import PlatformReceptionistBilling from "@/pages/platform/ReceptionistBilling";
import PlatformLogin from "@/pages/platform/PlatformLogin";
import PlatformResetPassword from "@/pages/platform/PlatformResetPassword";import {
  PlatformProtected, PlatformShell, PlatformOverview, PlatformBusinesses,
  PlatformTenant, PlatformAudit, PlatformRevenue, PlatformAnalytics,
  PlatformExports, PlatformSystem,
} from "@/pages/platform/PlatformConsole";
import {
  IntelligenceShell, IntelOverview, IntelPets, IntelCustomers, IntelGeography,
  IntelBusiness, IntelServices, IntelInventory, IntelGrooming, IntelSeasonality,
  IntelVeterinary, IntelBoarding, IntelTraining, IntelFinance,
  IntelBusinessDetail, IntelBranchDetail, IntelTrends, IntelBenchmark, IntelAnomalies, IntelDataQuality,
} from "@/pages/platform/Intelligence";
import DataExplorer from "@/pages/platform/DataExplorer";
import ExportCenter from "@/pages/platform/ExportCenter";
import CommandCenter from "@/pages/platform/CommandCenter";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <PawktSplash />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/activate" element={<Activate />} />
      <Route path="/platform-admin/login" element={<PlatformLogin />} />
      <Route path="/platform-admin/reset-password" element={<PlatformResetPassword />} />
      <Route path="/platform-admin" element={<PlatformProtected><PlatformShell /></PlatformProtected>}>
        <Route index element={<CommandCenter />} />
        <Route path="overview" element={<PlatformOverview />} />
        <Route path="businesses" element={<PlatformBusinesses />} />
        <Route path="businesses/:tenantId" element={<PlatformTenant />} />
        <Route path="revenue" element={<PlatformRevenue />} />
        <Route path="analytics" element={<PlatformAnalytics />} />
        <Route path="exports" element={<PlatformExports />} />
        <Route path="system" element={<PlatformSystem />} />
        <Route path="audit" element={<PlatformAudit />} />
        <Route path="phone-numbers" element={<PlatformPhoneNumbers />} />
        <Route path="shally-greetings" element={<ShallyGreetings />} />
        <Route path="shally-pilot" element={<ShallyPilot />} />
        <Route path="business-onboarding" element={<BusinessOnboarding />} />
        <Route path="business-outbound" element={<BusinessOutbound />} />
        <Route path="sales-leads" element={<PlatformSalesLeads />} />
        <Route path="receptionist-billing" element={<PlatformReceptionistBilling />} />
        <Route path="intelligence" element={<IntelligenceShell />}>
          <Route index element={<IntelOverview />} />
          <Route path="pets" element={<IntelPets />} />
          <Route path="customers" element={<IntelCustomers />} />
          <Route path="geography" element={<IntelGeography />} />
          <Route path="business" element={<IntelBusiness />} />
          <Route path="business/:tenantId" element={<IntelBusinessDetail />} />
          <Route path="business/:tenantId/branch/:branchId" element={<IntelBranchDetail />} />
          <Route path="services" element={<IntelServices />} />
          <Route path="inventory" element={<IntelInventory />} />
          <Route path="grooming" element={<IntelGrooming />} />
          <Route path="veterinary" element={<IntelVeterinary />} />
          <Route path="boarding" element={<IntelBoarding />} />
          <Route path="training" element={<IntelTraining />} />
          <Route path="finance" element={<IntelFinance />} />
          <Route path="trends" element={<IntelTrends />} />
          <Route path="compare" element={<IntelBenchmark />} />
          <Route path="anomalies" element={<IntelAnomalies />} />
          <Route path="data-quality" element={<IntelDataQuality />} />
          <Route path="seasonality" element={<IntelSeasonality />} />
        </Route>
        <Route path="explorer" element={<DataExplorer />} />
        <Route path="data-export" element={<ExportCenter />} />
      </Route>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/products" element={<Products />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/pet-compass" element={<PetCompassPage />} />
      </Route>
      <Route path="/book/:key" element={<BookPage />} />
      <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/self-booking" element={<Protected><SelfBooking /></Protected>} />
      <Route path="/*" element={<Protected><AppShell /></Protected>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
            <AppRouter />
          </ErrorBoundary>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
